import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import "moment/locale/en-gb";
import "moment/locale/es";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Arrow from "../../assets/img/arrow-left-grey-round.svg";
import DownloadIcon from "../../assets/img/downloadIcon.svg";
import Loader from "../../components/Loader";
import DecisionCard from "../../components/Property/DecisionCard";
import PolicyMetricSection from "../../components/Property/PolicyMetricSection";
import AuthContext from "../../context/auth/AuthContext";
import LandLordContext from "../../context/landlord/LandLordContext";
import {
  calculateFinalScore,
  calculateOverlayScore,
  getCustomIncomeDetails,
  getFieldForPolicies,
  getIdentityDetails,
  getRentPercentage,
  haveValue,
  resolveIncomeSelection,
} from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import "../../styles/linked-report-overview.css";
import { computeFinalResult } from "../../Utils/computeFinalResult";
import {
  SUB_SCORE_TRANSLATIONS,
  PILLAR_SUB_SCORES,
} from "../../constants/policyConstants";
import {
  getPillarBadgeFromScore,
  PILLAR_NO_DATA_KEY,
  PILLAR_TITLE_KEYS,
} from "../../helper/pillarStatus";

const formatDateOfBirth = (dob, currentLang) => {
  if (!dob) return currentLang === "es" ? "Sin datos" : "No data";

  const formattedDate = moment(dob).format("DD/MM/YYYY");
  return moment(formattedDate, "DD/MM/YYYY").isValid()
    ? formattedDate
    : currentLang === "es"
      ? "Sin datos"
      : "No data";
};

// Status styles for computeFinalResult
const STATUS_STYLES = {
  yellow: {
    backgroundColor: "#FFFBEC",
    color: "#E1BB20",
    border: ".1389vw solid #F2E09B",
  },
  green: {
    backgroundColor: "#F2FBE9",
    color: "#82CF3F",
    border: ".1389vw solid #DAECC8",
  },
  red: {
    backgroundColor: "#FFE2E2",
    color: "#FF3C3C",
    border: ".1389vw solid #EB9B9C",
  },
};

// Main component
const LinkedReportOverview = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId } = useParams();
  const {
    getPropertyDetails,
    getTenantExtraData,
    getRiskAssessmentReport,
    deaWorkspace,
    customPolicyDetails,
    getPolicyDetails,
  } = useContext(LandLordContext);
  const { user } = useContext(AuthContext);

  const queryParameters = new URLSearchParams(window.location.search);
  const propertyId = queryParameters.get("property");
  const sharedProfileIds = queryParameters.getAll("sharedprofileid");
  const reportStateKey = queryParameters.get("reportStateKey");
  const rent = Number(queryParameters.get("rent") || 0);
  const cachedReportState = useMemo(() => {
    if (!reportStateKey) return null;

    try {
      const cachedValue = sessionStorage.getItem(reportStateKey);
      if (!cachedValue) return null;

      const parsedValue = JSON.parse(cachedValue);
      if (!parsedValue?.expiresAt || parsedValue.expiresAt <= Date.now()) {
        sessionStorage.removeItem(reportStateKey);
        return null;
      }

      return parsedValue;
    } catch (error) {
      console.log("[LinkedReportOverview] cache read error", error);
      return null;
    }
  }, [reportStateKey]);

  const [addressVal, setAddressVal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState("loading");
  const [profiles, setProfiles] = useState([]);
  const [decisionReasons, setDecisionReasons] = useState([]);
  const [status, setStatus] = useState("declined");
  const [combinedIncome, setCombinedIncome] = useState(0);
  const [combinedEffortRate, setCombinedEffortRate] = useState("No data");
  const [lastVerdict, setLastVerdict] = useState(null);
  const [lastRecommendationFlags, setLastRecommendationFlags] = useState({});
  const [localReport, setLocalReport] = useState(null);
  const [apiReport, setApiReport] = useState(null);
  const [cachedInsurableStatus, setCachedInsurableStatus] = useState(null);
  const [showSpinner, setShowSpinner] = useState(false);

  // Current language checker
  const currentLang = useMemo(
    () =>
      (localStorage.getItem("i18nextLng")?.toLowerCase() || "en-us") === "en-us"
        ? "en"
        : "es",
    []
  );

  const [giveInsurance, setGiveInsurance] = useState(false);

  const todaysDate = useMemo(
    () =>
      (locale = localStorage.getItem("i18nextLng")) => {
        moment.locale(locale);
        return moment().format("D MMM YYYY");
      },
    []
  );

  const getIncomeForRecord = useMemo(
    () => (record, incomeSelection, customIncomeSources) => {
      const income = getCustomIncomeDetails(
        record,
        user,
        _t,
        incomeSelection,
        undefined,
        undefined,
        deaWorkspace,
        customIncomeSources
      );
      return Number(income?.amount || 0);
    },
    [_t, user, deaWorkspace]
  );

  useEffect(() => {
    if (!cachedReportState || !reportStateKey) return;

    const removeCache = () => sessionStorage.removeItem(reportStateKey);
    const remainingTime = Math.max(cachedReportState.expiresAt - Date.now(), 0);
    const timeoutId = window.setTimeout(removeCache, remainingTime);

    return () => window.clearTimeout(timeoutId);
  }, [cachedReportState, reportStateKey]);

  useEffect(() => {
    if (cachedReportState) {
      const cachedLocalReport = cachedReportState.localReport ?? null;
      const cachedApiReport = cachedReportState.apiReport ?? null;

      setAddressVal(cachedReportState.addressVal ?? null);
      setCombinedIncome(Number(cachedReportState.combinedIncome || 0));
      setCombinedEffortRate(cachedReportState.combinedEffortRate ?? "No data");
      setCachedInsurableStatus(
        cachedReportState.reportInsurableStatus === "simulationDidNotRun"
          ? null
          : typeof cachedReportState.reportInsurableStatus === "boolean"
            ? cachedReportState.reportInsurableStatus
            : typeof cachedReportState.areAllSelectedTenantsInsurable ===
                "boolean"
              ? cachedReportState.areAllSelectedTenantsInsurable
              : null
      );
      setLocalReport(cachedLocalReport);
      setDecisionReasons(cachedLocalReport?.report?.recommendation ?? []);
      setStatus(
        cachedLocalReport?.report?.case?.decision === "Approved"
          ? "approved"
          : cachedLocalReport?.report?.case?.decision === "Rejected"
            ? "declined"
            : "review"
      );
      setLoading(false);

      if (cachedApiReport) {
        setApiReport(cachedApiReport);
        setReportLoading("loaded");
        return;
      }

      if (cachedLocalReport) {
        setReportLoading("loading");
        getRiskAssessmentReport(cachedLocalReport)
          .then((response) => {
            if (response?.data?.report) {
              setApiReport(response.data.report);
              setReportLoading("loaded");
            } else {
              setApiReport(cachedLocalReport.report);
              setReportLoading("loaded");
            }
          })
          .catch((error) => {
            console.log("[LinkedReportOverview] cached API error", error);
            setApiReport(cachedLocalReport.report);
            setReportLoading("loaded");
          });
        return;
      }

      setReportLoading("failed");
      return;
    }

    if (!Object.keys(customPolicyDetails?.scoringConfig || {}).length) {
      return;
    }
    const loadData = async () => {
      setLoading(true);
      setReportLoading("loading");
      try {
        let propertyData = null;
        if (haveValue(propertyId)) {
          const propertyRes = await getPropertyDetails(propertyId);
          propertyData = propertyRes?.properties ?? null;
          setAddressVal(propertyData);
        }

        const tenantRes = await getTenantExtraData(sharedProfileIds);
        const rawTenants = tenantRes?.success_res ?? [];
        const guarantorDataArr = [];

        // #NOTE: get all the incomes of tenants and guarantors concurrently
        const incomesData = await Promise.all(
          rawTenants.map(async (tenant) => {
            const incomeSelection = resolveIncomeSelection(
              tenant?.income_selection,
              tenant?.id ?? tenant?.tenant_id ?? tenant?._id
            );

            const tenantIncome = getIncomeForRecord(
              tenant,
              tenant?.income_selection?.tenant_source,
              tenant?.custom_income_sources
            );
            tenant._localIncome = tenantIncome;

            const guarantors = tenant?.gurantor_id || [];
            const guarantorIncomeSelection =
              incomeSelection?.[tenant?._id]?.guarantor_sources ??
              incomeSelection?.guarantor_sources;
            const guarantorResults = await Promise.all(
              guarantors.map(async (g) => {
                const guarantorIncomeObj = getCustomIncomeDetails(
                  g,
                  user,
                  _t,
                  guarantorIncomeSelection?.[g?._id],
                  undefined,
                  undefined,
                  deaWorkspace,
                  tenant?.custom_income_sources
                );

                const guarantorIncome = guarantorIncomeObj?.amount ?? 0;
                g._localIncome = guarantorIncome;

                // 🔹 Step A: guarantor-only effort rate
                const guarantorOnlyEffortRate =
                  guarantorIncome > 0
                    ? (getRentPercentage(guarantorIncome, rent)?.roundValue ??
                      0)
                    : 0;

                // 🔹 Step B: scoring
                const gTemp = await getFieldForPolicies(
                  g,
                  user,
                  guarantorOnlyEffortRate,
                  giveInsurance,
                  deaWorkspace
                );

                const gScoreResult = await calculateFinalScore(
                  customPolicyDetails.scoringConfig,
                  customPolicyDetails.overlayConfig,
                  customPolicyDetails.policyConfig,
                  gTemp
                );

                const guarantorScore = gScoreResult?.finalScore ?? 0;

                // 🔹 Step C: weight
                let weight = 1;

                if (guarantorScore < 65) {
                  weight = 0.6;
                } else if (guarantorScore <= 80) {
                  weight = 0.75;
                }

                // de-dupeded gurantor calculation from below
                const gErrors = Object.entries(gTemp).filter(
                  ([_, value]) => value?.isHardFail === true
                );

                const label = haveValue(g?.forensic_name)
                  ? g?.forensic_name
                  : haveValue(g?.onfido_user_kyc?.onfido_doc_data?.first_name)
                    ? `${g?.onfido_user_kyc?.onfido_doc_data?.first_name} ${
                        g?.onfido_user_kyc?.onfido_doc_data?.last_name ?? ""
                      }`
                    : `${g?.fname ?? "N/A"} ${g?.lname ?? ""}`;

                gTemp.label = label;

                const result = await calculateFinalScore(
                  customPolicyDetails.scoringConfig,
                  customPolicyDetails.overlayConfig,
                  customPolicyDetails.policyConfig,
                  gTemp
                );

                result.errorEntries = gErrors;
                result.isHardFail = gErrors?.length > 0;

                console.log("GG", g);
                const guarantorTenantData = {
                  documentNumber: getIdentityDetails(g, g?.onfido_user_kyc)
                    ?.value,
                  dob: tenant?.dob,
                  income: g._localIncome,
                };

                guarantorDataArr.push({
                  _id: g._id,
                  data: {
                    ...result,
                    vals: gTemp,
                    _id: g._id,
                    tenantData: guarantorTenantData,
                    label: label,
                    isGuarantor: true, // Mark as guarantor for proper identification
                    role: "guarantor", // Explicit role for UI rendering
                  },
                });

                return guarantorIncome * weight;
              })
            );

            return {
              tenantIncome,
              guarantorIncomeSum: guarantorResults.reduce(
                (sum, val) => sum + val,
                0
              ),
            };
          })
        );

        const tenantIncomeSum = incomesData.reduce(
          (sum, data) => sum + data.tenantIncome,
          0
        );
        const guarantorIncomeSum = incomesData.reduce(
          (sum, data) => sum + data.guarantorIncomeSum,
          0
        );

        const totalIncome = tenantIncomeSum + guarantorIncomeSum;
        const finalEffortRate =
          getRentPercentage(totalIncome, rent)?.roundValue ?? "0";

        // Set combined income and effort rate
        setCombinedIncome(totalIncome);
        setCombinedEffortRate(
          typeof finalEffortRate === "number"
            ? `${finalEffortRate}%`
            : "No data"
        );

        // #NOTE: collect the profiles data for the payload concurrently
        const processedTenants = await Promise.all(
          rawTenants.map(async (tenant) => {
            console.log("tenant sharedprofile", tenant);
            const temp = await getFieldForPolicies(
              tenant,
              user,
              finalEffortRate,
              giveInsurance,
              deaWorkspace
            );

            const errorEntries = Object.entries(temp).filter(
              ([_, value]) => value?.isHardFail === true
            );

            let guarantorOutsideSpain = false;
            const checkGuarantors = tenant?.gurantor_id || [];
            for (let g of checkGuarantors) {
              const forensicData =
                g?.tenant_extra_field_id?.forensic_financial_data;
              if (forensicData) {
                for (let key in forensicData) {
                  if (
                    forensicData[key]?.origin &&
                    forensicData[key]?.origin !== "Local"
                  ) {
                    guarantorOutsideSpain = true;
                    break;
                  }
                }
              }
              if (guarantorOutsideSpain) break;
            }
            if (guarantorOutsideSpain) {
              errorEntries.push([
                "GUARANTOR_OUTSIDE_SPAIN",
                { isHardFail: true, label: "guarantorLivingOutsideSpain" },
              ]);
            }

            // ===== LABEL (UNCHANGED) =====
            const label = haveValue(tenant?.forensic_name)
              ? tenant?.forensic_name
              : haveValue(tenant?.onfido_user_kyc?.onfido_doc_data?.first_name)
                ? `${tenant?.onfido_user_kyc?.onfido_doc_data?.first_name} ${
                    tenant?.onfido_user_kyc?.onfido_doc_data?.last_name ?? ""
                  }`
                : `${tenant?.fname ?? "N/A"} ${tenant?.lname ?? ""}`;

            tenant.label = label;

            temp.EFFORT_RATE_SCORE = temp?.EFFORT_RATE_SCORE?.EFFORT_RATE_SCORE;

            const result = await calculateFinalScore(
              customPolicyDetails.scoringConfig,
              customPolicyDetails.overlayConfig,
              customPolicyDetails.policyConfig,
              temp
            );

            result.errorEntries = errorEntries;
            result.isHardFail = errorEntries?.length > 0;
            const tenantDataObj = {
              documentNumber: getIdentityDetails(
                tenant,
                tenant?.onfido_user_kyc
              )?.value,
              dob: tenant?.dob,
              income: tenant._localIncome,
              displayEffortRate: getRentPercentage(tenant._localIncome, rent)
                ?.roundValue,
            };

            return {
              tenantId: tenant?._id,
              tenantData: {
                ...result,
                vals: temp,
                _id: tenant?._id,
                tenantData: tenantDataObj,
                label: tenant?.label ?? "",
                isGuarantor: false, // Mark as tenant
                role: "tenant", // Explicit role for UI rendering
              },
            };
          })
        );

        const tempTenantResults = {};
        const guarantorResults = {};

        processedTenants.forEach((pt) => {
          if (pt.tenantId) {
            tempTenantResults[pt.tenantId] = pt.tenantData;
          }
        });
        guarantorDataArr.forEach((gData) => {
          guarantorResults[gData._id] = gData.data;
        });

        // console.log("tempTenantResults", tempTenantResults);
        // console.log("guarantorResults", guarantorResults);
        // console.log(
        //   `Combined: ${Object.keys(tempTenantResults).length} tenants, ${Object.keys(guarantorResults).length} guarantors`,
        // );

        // // Debug: Check the data structure before sending to API
        // console.log("Sample tenant data:", Object.values(tempTenantResults)[0]);
        // console.log(
        //   "Sample guarantor data:",
        //   Object.values(guarantorResults)[0],
        // );

        // 🎯 Use computeFinalResult to get the final decision locally (including guarantors)
        const { result: localReportResult, sideEffects } = computeFinalResult({
          tenantResults: tempTenantResults,
          selectedTenant: rawTenants,
          customPolicyDetails: customPolicyDetails,
          // giveInsurance was being used earlier and now it is not being used
          giveInsurance: false,
          finalEffortRate: finalEffortRate,
          enabled: false,
          deaWorkspace: deaWorkspace,
          user: user,
          lastVerdict: lastVerdict,
          lastRecommendationFlags: lastRecommendationFlags,
          STATUS_STYLES: STATUS_STYLES,
          translate: _t,
          isReport: true,
          guarantorResults: guarantorResults, // Pass guarantor results
        });

        // console.log("Local report result:", localReportResult);
        // console.log("Side effects:", sideEffects);

        // Update state with the local results
        if (sideEffects.verdict) {
          setLastVerdict(sideEffects.verdict);
          setStatus(
            sideEffects.verdict === "Approved"
              ? "approved"
              : sideEffects.verdict === "Rejected"
                ? "declined"
                : "review"
          );
        }
        if (sideEffects.recommendationFlags) {
          setLastRecommendationFlags(sideEffects.recommendationFlags);
        }
        if (localReportResult?.report?.recommendation) {
          setDecisionReasons(localReportResult.report.recommendation);
        }
        if (localReportResult) {
          setLocalReport(localReportResult);
        }

        // 🎯 Call API to get enhanced report (like RiskAssessmentReport does)
        if (localReportResult) {
          const apiResponse = await getRiskAssessmentReport(localReportResult);
          console.log("API response:", apiResponse);

          if (apiResponse?.data?.report) {
            setApiReport(apiResponse.data.report);
            setReportLoading("loaded");
          } else {
            setReportLoading("failed");
            // Fallback to local report if API fails
            setApiReport(localReportResult.report);
          }
        } else {
          setReportLoading("failed");
        }
      } catch (err) {
        console.log("[LinkedReportOverview] error", err);
        setReportLoading("failed");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [customPolicyDetails, cachedReportState]);

  useEffect(() => {
    getPolicyDetails();
  }, []);

  console.log("localReport", localReport);

  // Handle retry for API call
  const retryApiCall = () => {
    if (localReport) {
      setReportLoading("loading");
      getRiskAssessmentReport(localReport)
        .then((response) => {
          if (response?.data?.report) {
            setApiReport(response.data.report);
            setReportLoading("loaded");
          } else {
            setReportLoading("failed");
          }
        })
        .catch((error) => {
          console.log("API retry error:", error);
          setReportLoading("failed");
        });
    }
  };

  const downloadReportPdf = async () => {
    const container = document.getElementById("divToPrint");
    if (!container) return;

    const hiddenElements = document.querySelectorAll(".no-print");

    try {
      setShowSpinner(true);

      const A4_WIDTH = 595.28;
      const A4_HEIGHT = 841.89;
      const PDF_MARGIN = 24;

      hiddenElements.forEach((el) => (el.style.display = "none"));

      const pdf = new jsPDF({
        orientation: "p",
        unit: "pt",
        format: "a4",
      });

      const canvas = await html2canvas(container, {
        useCORS: true,
        backgroundColor: "#ffffff",
        scale: 2,
        ignoreElements: (element) => element.classList.contains("no-print"),
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const scale = Math.min(
        (A4_WIDTH - PDF_MARGIN * 2) / imgWidth,
        (A4_HEIGHT - PDF_MARGIN * 2) / imgHeight
      );

      const renderWidth = imgWidth * scale;
      const renderHeight = imgHeight * scale;

      const x = (A4_WIDTH - renderWidth) / 2;
      const y = PDF_MARGIN;

      pdf.addImage(imgData, "JPEG", x, y, renderWidth, renderHeight);

      // Format address for filename
      const addressParts = [
        addressVal?.street,
        addressVal?.street_number,
        addressVal?.floor_number,
        addressVal?.flat_number,
        addressVal?.postcode,
        addressVal?.muncipality,
      ]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, "_");

      // Get tenant names for filename
      const tenantNames = applicants
        .filter((applicant) => applicant.role !== "guarantor")
        .map((applicant) => {
          const name = applicant.name || "";
          return name.replace(/\s+/g, "_");
        })
        .join("_");

      // Generate filename in format: address_tenant1_tenant2_tenantX.pdf
      const filename = `${addressParts}${tenantNames ? "_" + tenantNames : ""}.pdf`;

      pdf.save(filename);

      setShowSpinner(false);
    } catch (err) {
      console.error("Error capturing element:", err);
      setShowSpinner(false);
    } finally {
      hiddenElements.forEach((el) => (el.style.display = ""));
    }
  };

  const tenantProfiles = useMemo(
    () => profiles.filter((entry) => entry.role === "tenant"),
    [profiles]
  );
  const guarantorProfiles = useMemo(
    () => profiles.filter((entry) => entry.role === "guarantor"),
    [profiles]
  );

  // Get decision from API report or fall back to local status
  const getDecisionStatus = () => {
    if (apiReport?.decision) {
      const decision = apiReport.decision["en"] || apiReport.decision;
      return decision;
    }
    return status === "approved"
      ? "Approved"
      : status === "declined"
        ? "Rejected"
        : "Conditional";
  };

  const decisionTitle = getDecisionStatus();

  // Get applicants from API report or fall back to local report
  const getApplicants = useCallback(() => {
    if (apiReport?.applicants) {
      // return apiReport.applicants;
      return apiReport.applicants.map((a) => {
        const applicant = localReport?.report?.applicants?.find(
          (la) => la._id === a._id
        );
        return {
          ...applicant,
          ...a,
        };
      });
    }
    if (localReport?.report?.applicants) {
      return localReport.report.applicants;
    }
    return [];
  }, [apiReport, localReport]);

  const applicants = useMemo(() => getApplicants(), [getApplicants]);
  console.log("applicants", applicants);

  useEffect(() => {
    if (!applicants?.length) {
      setProfiles([]);
      return;
    }

    setProfiles(
      applicants.map((applicant) => ({
        role: applicant?.role || "tenant",
        isInsurable: applicant?.isInsurable !== false,
      }))
    );
  }, [applicants]);
  // Transform API applicant data to profile format for UI
  const transformApplicantToProfile = (applicant) => {
    const initials = applicant.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

    let sections = [];
    let documentation = {
      title: _t(PILLAR_TITLE_KEYS.documentationStrength),
      badge: {
        label: _t(PILLAR_NO_DATA_KEY),
        variant: "neutral",
      },
      rows: [],
      docs: applicant.subScores?.documentation?.missingDocs || [],
    };

    const pillarToSubScoreKey = {
      incomeAffordability: "affordability",
      employmentStability: "employment stability",
      financialBehaviour: "financial behaviour",
      creditDefaultRisk: "default risk",
    };

    if (applicant.pillars) {
      Object.entries(applicant.pillars).forEach(([key, value]) => {
        const badge = getPillarBadgeFromScore(key, value);

        if (key === "documentationStrength") {
          documentation.title = _t(PILLAR_TITLE_KEYS.documentationStrength);
          documentation.badge = {
            label: _t(badge.labelKey),
            variant: badge.variant,
          };
          return;
        }

        const rows = [];
        const relevantSubScores =
          PILLAR_SUB_SCORES[pillarToSubScoreKey[key]] || [];

        relevantSubScores.forEach((subKey) => {
          const subScore = applicant.subScores?.[subKey];
          if (subScore) {
            const hasDisplayEffortRate =
              subKey === "effortRate" &&
              applicant?.tenantData?.displayEffortRate !== undefined &&
              applicant?.tenantData?.displayEffortRate !== null;
            rows.push({
              label:
                SUB_SCORE_TRANSLATIONS[subKey]?.[currentLang] || _t(subKey),
              value: hasDisplayEffortRate
                ? `${applicant.tenantData.displayEffortRate}%`
                : subScore.label || subScore.score || _t(PILLAR_NO_DATA_KEY),
              muted: !hasDisplayEffortRate && !subScore.label,
            });
          }
        });

        sections.push({
          title: _t(PILLAR_TITLE_KEYS[key] || key),
          badge: { label: _t(badge.labelKey), variant: badge.variant },
          rows,
        });
      });
    }

    return {
      id: applicant._id,
      initials: initials,
      label: applicant.name,
      role: applicant.role || "tenant",
      documentNumber:
        applicant?.tenantData?.documentNumber ??
        (currentLang === "es" ? "Sin datos" : "No data"),
      dob: formatDateOfBirth(
        applicant?.tenantData?.dob ??
          (currentLang === "es" ? "Sin datos" : "No data"),
        currentLang
      ),
      income:
        applicant?.tenantData?.income ??
        (currentLang === "es" ? "Sin datos" : "No data"),
      isInsurable: applicant.isInsurable !== false,
      sections: sections,
      documentation: documentation,
    };
  };

  const renderCard = (profile) => (
    <article className="linked-report__card" key={profile.id}>
      <div className="linked-report__card-header">
        <div className="linked-report__avatar">{profile.initials || "NA"}</div>
        <div>
          <div className="linked-report__person-name-row">
            <div className="linked-report__person-name">{profile.label}</div>
            <span
              style={{
                color: profile.isInsurable ? "#82CF3F" : "#E1BB20",
                backgroundColor: profile.isInsurable ? "#F2FBE9" : "#FFFBEC",
              }}
              className="report-overview__badge"
            >
              <>
                {profile.isInsurable ? (
                  <img src={"/images/shield.svg"} alt="" />
                ) : (
                  ""
                )}
                {_t(profile.isInsurable ? "insurable" : "notInsurable")}
              </>
            </span>
          </div>
          <div className="linked-report__person-meta">
            {`${profile.documentNumber} • ${formatDateOfBirth(profile.dob, currentLang)} • ${_t("income")}: ${profile.income}`}
          </div>
        </div>
      </div>

      <div className="linked-report__metrics-grid linked-report-overview__metrics-grid">
        {profile.sections.map((section) => (
          <PolicyMetricSection
            key={`${profile.id}-${section.title}`}
            title={section.title}
            badge={section.badge}
            rows={section.rows}
          />
        ))}
      </div>

      <div className="linked-report__documentation">
        <div className="linked-report__metric-header">
          <h3>{profile.documentation.title}</h3>
          <span
            className={`linked-report__status-pill linked-report__status-pill--${profile.documentation.badge.variant}`}
          >
            {profile.documentation.badge.label}
          </span>
        </div>
        {profile.documentation.rows.map((row) => (
          <div
            className="linked-report__row"
            key={`${profile.id}-doc-${row.label}`}
          >
            <span>{row.label}</span>
            <span>{row.value}</span>
          </div>
        ))}
        <ul className="linked-report__doc-list">
          {profile.documentation.docs.map((doc) => (
            <li key={`${profile.id}-${doc}`}>{doc}</li>
          ))}
        </ul>
      </div>
    </article>
  );

  if (loading) return <Loader />;

  // Show API loading state if still fetching from API
  if (reportLoading === "loading") {
    return (
      <div className="linked-report">
        <div className="linked-report__container">
          <div className="risk-report-state-container">
            <div className="risk-report-state-icon risk-report-state-icon--spinning">
              <svg
                className="spinner"
                width="32"
                height="32"
                viewBox="0 0 32 32"
              >
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  fill="none"
                  stroke="#A3EB65"
                  strokeWidth="3"
                  strokeOpacity="0.3"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  fill="none"
                  stroke="#A3EB65"
                  strokeWidth="3"
                  strokeDasharray="80"
                  strokeLinecap="round"
                >
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 16 16"
                    to="360 16 16"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </circle>
              </svg>
            </div>
            <p className="risk-report-state-title">{_t("generatingReport")}</p>
            <p className="risk-report-state-desc">
              {_t("wereAnalyzingTheApplicationThisMayTakeAFewSeconds")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if API call failed
  if (reportLoading === "failed") {
    return (
      <div className="linked-report">
        <div className="linked-report__container">
          <div className="risk-report-state-container">
            <div className="risk-report-state-icon risk-report-state-icon--error">
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                stroke="#FF3C3C"
                strokeWidth="2"
              >
                <circle cx="16" cy="16" r="14" />
                <path d="M10 16L14 20L22 12" />
              </svg>
            </div>
            <p className="risk-report-state-title">
              {_t("failedToLoadReport")}
            </p>
            <p className="risk-report-state-desc">
              {_t(
                "somethingWentWrongWhileGeneratingTheRiskAssessmentPleaseTryAgain"
              )}
            </p>
            <button className="risk-report-retry-btn" onClick={retryApiCall}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M23 4v6h-6" />
                <path d="M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              {_t("retry")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="linked-report">
      <div className="linked-report__container" id="divToPrint">
        {/* header with date and back button */}
        <div className="report-overview__header">
          <div
            className="report-overview__back cursor-pointer no-print"
            onClick={() =>
              navigate(workspaceId ? `/${workspaceId}/home` : "/home")
            }
          >
            <img src={Arrow} alt="" />
            <span>{_t("goBack")}</span>
          </div>

          <button
            className="btnNext no-print linked-report__download-btn"
            disabled={showSpinner}
            onClick={downloadReportPdf}
          >
            {!showSpinner && <img src={DownloadIcon} />}
            {showSpinner ? `${_t("downloading")}...` : _t("downloadReport")}
          </button>

          <h1 className="report-overview__title">
            {[
              `${addressVal?.street ?? ""} ${addressVal?.street_number ?? ""}`.trim(),
              `${addressVal?.floor_number ? addressVal?.floor_number : ""} ${
                addressVal?.flat_number ? "-" + addressVal?.flat_number : ""
              }`,
              addressVal?.province,
            ]
              .filter(Boolean)
              .join(", ")}
          </h1>

          <div className="report-overview__subinfo">
            <span>
              {[addressVal?.postcode, addressVal?.muncipality]
                .filter(Boolean)
                .join(", ")}
            </span>
            <span className="report-overview__dot">•</span>
            <span>€{rent}/month</span>
          </div>

          <div className="report-overview__meta">
            <div className="report-overview__meta-item">
              <img src={"/images/calendar-icon.svg"} alt="" />
              <span>{todaysDate()}</span>
            </div>
            <div className="report-overview__meta-item">
              <img src={"/images/multiple-user.svg"} alt="" />
              <span>
                {sharedProfileIds?.length} {_t("tenantsSelected")}
              </span>
            </div>
          </div>
        </div>

        {/* decision card */}
        <DecisionCard
          decisionTitle={_t(decisionTitle)}
          decisionStatus={decisionTitle}
          combinedIncome={combinedIncome}
          combinedEffortRate={combinedEffortRate}
          decisionSummary={apiReport?.decisionSummary}
          insurable={cachedInsurableStatus}
          currentLang={currentLang}
        />

        <h2 className="linked-report__section-title">{_t("tenants")}</h2>
        {applicants
          .filter((applicant) => applicant.role !== "guarantor")
          .map(transformApplicantToProfile)
          .map(renderCard)}

        <h2 className="linked-report__section-title">{_t("guarantors")}</h2>
        {applicants.filter((applicant) => applicant.role === "guarantor")
          .length > 0 ? (
          applicants
            .filter((applicant) => applicant.role === "guarantor")
            .map(transformApplicantToProfile)
            .map(renderCard)
        ) : (
          <article className="linked-report__card">
            <div className="linked-report__documentation">
              <div className="linked-report__row">
                <span>{_t("noGuarantorsFound")}</span>
                <span className="linked-report__muted">
                  {currentLang === "es" ? "Sin datos" : "No data"}
                </span>
              </div>
            </div>
          </article>
        )}
      </div>
    </div>
  );
};

export default LinkedReportOverview;
